import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { CookieConsentFacade } from '@planone/shared/feature-cookie-consent';
import { enableYouTubeTracking } from '@snowplow/browser-plugin-youtube-tracking';
import { filter, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'planone-youtube-video-embed',
  templateUrl: './youtube-video-embed.component.html',
  styleUrls: ['./youtube-video-embed.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class YoutubeVideoEmbedComponent implements OnInit, OnDestroy {
  @Input() index?: number;

  @Input() width: unknown = '640';
  @Input() height: unknown = '360';
  @Input() frameborder: unknown = '0';

  @Input() path: unknown = '';
  @Input() updatePath: unknown = true;
  @Input() autoplay: unknown = true;
  @Input() allowTracking: unknown = false;
  @Input() allowYoutubeBranding: unknown = true;
  @Input() enableJsApi: unknown = true;
  @Input() showControls: unknown = true;
  @Input() showFullscreenControl: unknown = true;
  @Input() loop: unknown = false;
  @Input() showInfo: unknown = true;
  @Input() showSuggestions: unknown = true;
  @Input() startAt: unknown = 0;
  @Input() endAt: unknown = 0;

  @Input() title: unknown = 'YouTube video player';

  private unsubscribe: Subject<void> = new Subject<void>();

  constructor(private cookieConsentFacade: CookieConsentFacade) {}

  ngOnInit() {
    this.cookieConsentFacade.accepted$
      .pipe(
        takeUntil(this.unsubscribe),
        filter((cookieConsent?: boolean) => !!cookieConsent)
      )
      .subscribe(() => {
        setTimeout(() => {
          enableYouTubeTracking({
            id: 'youtubeVideo' + this.index,
            options: {
              captureEvents: ['play', 'pause', 'seek', 'ended', 'percentprogress', 'error'],
            },
          });
        });
      });

    if (this.updatePath) {
      this.path = this.transformYoutubeVideoLink(<string>this.path);

      const query: { [param: string]: string } = {};

      if (<number>this.startAt > 0) {
        query['start'] = <string>(<unknown>this.startAt);
      }

      if (<number>this.endAt > 0) {
        query['end'] = <string>(<unknown>this.endAt);
      }

      if (!this.showControls) {
        query['controls'] = '0';
      }

      if (this.autoplay) {
        query['autoplay'] = '1';
      }

      if (this.showFullscreenControl) {
        query['fs'] = '1';
      }

      if (this.loop) {
        query['loop'] = '1';
      }

      if (!this.showInfo) {
        query['showinfo'] = '0';
      }

      if (!this.showSuggestions) {
        query['rel'] = '0';
      }

      if (!this.allowYoutubeBranding) {
        query['modestbranding'] = '1';
      }

      if (this.enableJsApi) {
        query['enablejsapi'] = '1';
      }

      let i = 0;

      for (const property in query) {
        if (i === 0) {
          this.path += '?' + property + '=' + query[property];
        } else {
          this.path += '&' + property + '=' + query[property];
        }

        i++;
      }
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  transformYoutubeVideoLink(path: string): string {
    const regExp =
      /(?:http(?:s)?:\/\/)?(?:www\.)?(?:m\.)?(?:youtu\.be\/|youtube\.com\/(?:(?:watch)?\?(?:.*&)?v(?:i)?=|((?:embed|v|vi|user)\/)))([a-zA-Z0-9\-_]*)/gi;
    const match = [...(<any>path).matchAll(regExp)];
    if (match?.[0]?.[2]) {
      return 'https://www.youtube-nocookie.com/embed/' + match[0][2];
    } else {
      return 'https://www.youtube-nocookie.com/embed/';
    }
  }
}
